// The request front door (v1.81.0): "the ice machine is leaking" becomes one
// tiny screen — what's going on, an optional photo, send. Built for the person
// who has never opened a board in their life: one box, one button, and a
// promise that the answer comes back to them ("you'll hear back here").
// Full-screen (the house pattern for phone-first surfaces), never a floating
// dialog a thumb has to aim at.
import { useEffect, useRef, useState } from 'react';
import { Camera, Send, X } from 'lucide-react';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { useToastStore } from '@/core/ui/toastStore';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';

interface Door { areaId: number; name: string; label: string; lead?: string | null; featured?: boolean }

export function RequestDoor({ onClose }: { onClose: () => void }) {
  const toast = useToastStore((s) => s.show);
  const [doors, setDoors] = useState<Door[] | null>(null);
  const [doorId, setDoorId] = useState<number | ''>('');
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useEscapeToClose(onClose);

  useEffect(() => {
    api.get<{ doors: Door[] }>('/requests/doors')
      .then((r) => { setDoors(r.doors); if (r.doors.length === 1) setDoorId(r.doors[0]!.areaId); })
      .catch(() => setDoors([]));
  }, []);

  const door = doors?.find((d) => d.areaId === (doorId || (doors.length === 1 ? doors[0]?.areaId : '')));

  const send = async () => {
    const target = doorId || (doors?.length === 1 ? doors[0]?.areaId : undefined);
    if (!target || busy) return;
    if (text.trim().length < 5) { toast('Say a little more about what’s going on.', 'error'); return; }
    setBusy(true);
    try {
      const r = await api.post<{ taskId: number; name: string; teamName: string }>('/requests', { areaId: target, text: text.trim() });
      if (photo) {
        try {
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(String(fr.result));
            fr.onerror = () => reject(new Error('read failed'));
            fr.readAsDataURL(photo);
          });
          await api.post(`/tasks/${r.taskId}/attachments`, {
            name: photo.name || 'photo.jpg', dataBase64: dataUrl.split(',')[1],
            mimetype: photo.type || 'image/jpeg', silent: true,
          }, { timeout: 120000 });
        } catch {
          toast('Sent — but the photo didn’t upload. You can add it from the card.', 'error');
        }
      }
      toast(`Sent to the ${r.teamName} team — you’ll hear back here.`, 'info');
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not send that.', 'error');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[55] flex flex-col bg-page animate-fade-in">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h1 className="text-[15px] font-semibold text-ink">Ask a team</h1>
        <button onClick={onClose} className="rounded p-1.5 text-faint hover:text-ink" title="Close"><Icon i={X} size={16} /></button>
      </div>

      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col gap-4 overflow-y-auto p-4">
        {doors === null && <p className="text-[13px] text-muted">One second…</p>}
        {doors !== null && doors.length === 0 && (
          <p className="text-[13.5px] leading-relaxed text-muted">No team is taking requests right now — tell Allyson and it can be switched on.</p>
        )}
        {doors !== null && doors.length > 0 && (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What do you need? Say what, for whom, and by when — or what’s broken and where."
              rows={5}
              maxLength={2000}
              className="w-full resize-none rounded-lg border border-line bg-canvas p-3 text-[15px] leading-relaxed text-ink outline-none placeholder:text-faint focus:border-brand/60"
            />

            {doors.length > 1 && (
              <label className="text-[13px] text-muted">
                Which team?
                <select value={doorId} onChange={(e) => setDoorId(Number(e.target.value))}
                  className="mt-1 block w-full rounded-md border border-line bg-canvas px-2 py-2 text-[14px] text-ink outline-none">
                  <option value="">Pick a team…</option>
                  {doors.some((d) => d.featured) && (
                    <optgroup label="Quick">
                      {doors.filter((d) => d.featured).map((d) => <option key={d.areaId} value={d.areaId}>{d.label}</option>)}
                    </optgroup>
                  )}
                  <optgroup label="Every team">
                    {doors.filter((d) => !d.featured).map((d) => <option key={d.areaId} value={d.areaId}>{d.name}{d.lead ? ` — ${d.lead}` : ''}</option>)}
                  </optgroup>
                </select>
              </label>
            )}

            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
              <button onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-[13px] text-muted hover:bg-hover hover:text-ink">
                <Icon i={Camera} size={14} /> {photo ? 'Change photo' : 'Add a photo'}
              </button>
              {photo && <span className="min-w-0 truncate text-[12px] text-faint">{photo.name}</span>}
            </div>

            <button onClick={() => void send()} disabled={busy || text.trim().length < 5 || (doors.length > 1 && !doorId)}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-[14px] font-semibold text-page transition hover:brightness-110 disabled:opacity-50">
              <Icon i={Send} size={14} /> {busy ? 'Sending…' : 'Send it'}
            </button>

            {door && (
              <p className="text-[12px] leading-snug text-faint">
                This lands on the <strong>{door.name}</strong> board{door.lead ? <> ({door.lead}’s team)</> : null}. You’ll be able to open your card, and when someone takes it, finishes it, or can’t do it, you’ll get a note here — no chasing.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
